<template>
  <div :id="carouselID" class='carousel carousel-animated carousel-animate-slide'>
    <div class='carousel-container' :class="{'reverse-fix': reverseFix}" ref="container">
      <div
        v-for="(attachment, offset) in attachments"
        class='carousel-item has-background'
        :key="attachment.id"
        :class="{'is-active': offset === 0}"
        ref="items"
        @mouseover="showOptions = true"
        style="margin-bottom: 20px;"
      >
        <div v-if="!isTouch && attachment.type === 'image'"
             class="background-image no-print"
             v-bind:style="{ backgroundImage: 'url(' + attachment.url + ')'}" />
        <attachment :attachment="attachment"
                    :context="context"
                    :selectDot="selectDot"
                    :position="position"
                    :getPostDirection="getPostDirection"
                    :lengthOfAttach="attachments.length"
                    @togglePreview="showPreview" />
      </div>
    </div>
    <div class="carousel-navigation is-overlay no-print">
      <div class="carousel-nav-left" ref="navLeft" judge-on-boarding="step-2">
        <i class="fal fa-chevron-left" aria-hidden="true"></i>
      </div>
      <div class="carousel-nav-right" ref="navRight">
        <i class="fal fa-chevron-right" aria-hidden="true"></i>
      </div>
    </div>
    <div class="carousel-dots no-print" v-if="attachments.length > 1">
        <span v-for="(attachment, index) in attachments" :key="index" @click.prevent="selectDot(index)" :class="{selected: (index) === position}"></span>
    </div>
    <preview v-if="shouldShowPreview" v-bind="{src: preview, canAnnotate, project}" :canCreate="canCreate" :position="position" :showArrows="attachments.length > 1" @close="hidePreview" @annotate="annotate" @goToSlide="selectDot($event)"></preview>
    <options v-bind="{show: showOptions && !isMobile, canAnnotate, canPreview, canEdit, canCreate, project, post, isLocked}" @hide="showOptions = false" @expand="showPreview" @annotate="annotate" class="options" @edit="editEmit" @deleting="$emit('deleting')"></options>
  </div>
</template>

<script>
import BulmaCarousel from '@/plugins/bulma-carousel/js/'
import Attachment from './Attachment'
import Options from './Overlays/Options'
import Preview from './Overlays/Preview'

import componentState from '@/mixins/component-state'
import breakpoints from '@/libs/breakpoints'
import displayTypes from '@/mixins/project-display-types'

export default {
  components: {
    Attachment,
    Preview,
    Options
  },
  mixins: [componentState, displayTypes],
  props: ['attachments', 'isGrid', 'context', 'initialPosition', 'canCreate', 'activeTab', 'canEdit', 'project', 'post', 'getPostDirection'],
  data: function () {
    return {
      carousel: null,
      position: 0,
      direction: '',
      goToSlide: 0,
      listeners: { navLeft: [], navRight: [] },
      preview: null,
      showOptions: false,
      lengthOfAttach: this.attachments.length
    }
  },
  computed: {
    carouselID: function () {
      return `carousel${this._uid}`
    },
    numAttachments: function () {
      return this.attachments.length
    },
    reverseFix: function () {
      return this.direction === 'previous' && this.numAttachments === 2
    },
    isTouch: function () {
      return breakpoints.isTouch()
    },
    shouldShowPreview: function () {
      return !!this.preview
    },
    canPreview: function () {
      const attachment = this.attachments[this.position]
      if (!attachment) {
        return false
      }
      if (attachment.postID) {
        const project = this.$store.getters.getProjectByPostID(attachment.postID)
        return (attachment.type === 'image' || attachment.type === 'whiteboard') && (project && project.displayType !== this.DisplayTypeRegistrationForm)
      } else {
        return (attachment.type === 'image' || attachment.type === 'whiteboard')
      }
    },
    isLocked: function () {
      const attachment = this.attachments[this.position]
      if (!attachment) {
        return false
      }
      return attachment.locked
    },
    canAnnotate: function () {
      return this.canPreview
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    isTabActive: function () {
      if (!this.activeTab) {
        return true
      } else if (this.$route.path.includes('auditoriumStory')) {
        return true
      } else {
        return (this.isGrid && this._get(this.activeTab) === 'grid') || (!this.isGrid && this._get(this.activeTab) === 'list') || (this.isGrid && this._get(this.activeTab) === 'search') || (this.isGrid && this._get(this.activeTab) === 'list')
      }
    },
    isFeed: function () {
      return this.$route.name === 'UpdateFeed'
    }
  },
  watch: {
    numAttachments: function () {
      this.resetCarousel()
    },
    carousel: function () {
      if (this.carousel) {
        this.initCarousel()
      }
    },
    position: function () {
      this.$emit('position', this.position)
    },
    attachments: function () {
      this.resetCarousel()
    }
  },
  mounted: function () {
    if (this.initialPosition > 0) {
      this.goToSlide = this.initialPosition + 1
    }
    this.initAddEventOverride()

    if (this.isTabActive || this.isFeed) {
      this.attachCarousel()
    }
  },
  methods: {
    editEmit: function () {
      this.$emit('edit')
    },
    initAddEventOverride: function () {
      // bulma carousel doesn't cleanup the event listeners on the nav buttons
      // so we need a way to track them  so we can remove them when we re-init the canvas
      this.$refs.navLeft._addEventListener = this.$refs.navLeft.addEventListener
      this.$refs.navLeft.addEventListener = (type, listener, options) => {
        this.listeners.navLeft.push({ type, listener })
        this.$refs.navLeft._addEventListener(type, listener, options)
      }

      this.$refs.navRight._addEventListener = this.$refs.navRight.addEventListener
      this.$refs.navRight.addEventListener = (type, listener, options) => {
        this.listeners.navRight.push({ type, listener })
        this.$refs.navRight._addEventListener(type, listener, options)
      }
    },
    attachCarousel: function () {
      this.carousel = new BulmaCarousel(`#${this.carouselID}`)
    },
    initCarousel: function () {
      this.carousel.on('carousel:slide:after', this.slideAfter.bind(this))
      this.setCarouselItemPositions()
      this.trackCarouselSlideDirection()

      if (this.goToSlide) {
        this.selectDot(this.goToSlide)
        this.goToSlide = 0
      }
    },
    resetCarousel: function () {
      this.listeners.navLeft.forEach((o) => {
        this.$refs.navLeft.removeEventListener(o.type, o.listener)
      })
      this.listeners.navLeft = []

      this.listeners.navRight.forEach((o) => {
        this.$refs.navRight.removeEventListener(o.type, o.listener)
      })
      this.listeners.navRight = []

      if (this.carousel) {
        this.carousel.destroy()
      }

      this.carousel = null

      this.direction = ''
      this.$refs.navLeft.style.display = 'flex'
      this.$refs.navRight.style.display = 'flex'

      this.$refs.container.style.cssText = ''

      if (this.$refs.items) {
        this.$refs.items.forEach((item) => {
          item.style.cssText = ''
        })
      }

      this.$nextTick(() => {
        this.attachCarousel()
        this.goToSlide = this.numAttachments
      })
    },
    setCarouselItemPositions: function () {
      Array.prototype.forEach.call(this.carousel.items, (item, offset) => {
        this.carousel.items[offset].pos = offset
      })
    },
    trackCarouselSlideDirection: function () {
      this.carousel.__slide = this.carousel._slide
      this.carousel._slide = (direction) => {
        if (this.carousel.items.length > 1) {
          this.direction = direction
          this.carousel.__slide(direction)
        }
      }
    },
    selectDot: function (selectedSlide) {
      if (!this.carousel) {
        return
      }

      const movingRight = selectedSlide === (this.position + 1)

      if (selectedSlide < 0) {
        selectedSlide = this.carousel.items.length - 1
      } else if (selectedSlide >= this.carousel.items.length) {
        selectedSlide = 0
      }

      const attachment = this.attachments[selectedSlide]
      if (this.preview !== null && (!attachment || (attachment.type !== 'image' && attachment.type !== 'whiteboard'))) {
        if (movingRight) {
          this.position += 1
          this.selectDot(selectedSlide + 1)
        } else {
          this.position -= 1
          this.selectDot(selectedSlide - 1)
        }
        return
      }

      const item = this.carousel.items[selectedSlide] || null
      if (!item) {
        return
      }

      if (this.carousel.currentItem.node) {
        this.carousel.currentItem.node.classList.remove('is-active')
      }

      this.carousel.currentItem.node = item
      this.carousel.currentItem.node.classList.add('is-active')

      this.carousel._setOrder()
      this.position = item.pos

      if (this.preview !== null) {
        this.showPreview()
      }
    },
    slideAfter: function (item) {
      this.position = item.node.pos
      if (this.reverseFix) {
        // edge-case where next item needs to be to the right
        this.carousel.oldItemNode.style.order = 2
      }
      if (this.preview !== null) {
        this.showPreview()
      }
    },
    showPreview: function () {
      const attachment = this.attachments[this.position]
      if (!attachment || (attachment.type !== 'image' && attachment.type !== 'whiteboard')) {
        return
      }

      this.preview = attachment.url

      // if we're part of a state group, let them know the modal state
      // needed for the safari modal fix in comments
      if (this.stateObjectKey) {
        this._set('carouselPreview', true)
      }
    },
    hidePreview: function () {
      this.preview = null

      // if we're part of a state group, let them know the modal state
      // needed for the safari modal fix in comments
      if (this.stateObjectKey) {
        this._set('carouselPreview', false)
      }
    },
    annotate: function (type) {
      if (!this.canAnnotate) {
        return
      }

      this.$emit('annotate', type, this.position)
    }
  }
}

</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  @import "@/plugins/bulma-carousel/sass/index.sass";

  .carousel{
    border-top: 2px solid whitesmoke;
    border-bottom: 2px solid whitesmoke;
    position: relative;
    max-width: 100%;
    margin-bottom: 0.5rem;
    flex-grow: 2;
    @include mobile {
      max-width: 100%;
      left: 0;
    }
    .background-image{
      position:absolute;
      z-index: -1;
      height:100%;
      width:100%;
      filter:blur(75px) saturate(1.5);
      opacity: 0.4;
    }
    .carousel-item {
      max-width: 100%;
      min-height: 4rem;
      height: 0;
      transition: height 0.5s;
      &.has-background {
        overflow: hidden;
      }
      &.is-active {
        height: 100%;
      }
    }
    .carousel-dots{
      position: absolute;
      bottom: -22px;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 4;
      white-space: nowrap;
      @include mobile {
        bottom: -22px;
      }

      span {
        cursor: pointer;
        border-radius: 50%;
        height: 15px;
        width: 15px;
        display: inline-block;
        background: white;
        box-shadow: $default-shadow;
        margin: 0.4rem;

        @include mobile {
          margin: 0.3rem;
        }

        &.selected {
          border: 2px solid white;
          background: $primary;
        }
      }
    }

    .reverse-fix {
      margin-left: 100%;
    }

    .carousel-navigation {
      &.is-overlay {
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 9;
      }
    }
}
</style>
